import * as React from "react";
import { useHistory } from "react-router-dom";
import { EditPromptUI } from "../prompt/EditPromptUI";
import { useAppController } from "../../hooks/useAppController";
import { DEFAULT_PROMPT } from "../../../shared/constants";
import { extractSelectedData } from "../../../shared/selectedDataExtractor";
import { extractPivotTableDataIfPresent } from "../../../shared/pivotTableDataExtractor";
import { savePromptToHistory } from "../../../shared/apiService";

const EditPromptRoute: React.FC = () => {
  const history = useHistory();
  const controller = useAppController();
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    // Only run initialization once when templates are loaded
    if (controller.savedPromptTemplates.length === 0 || isInitialized) {
      return;
    }

    console.log("🔄 Running initialization logic");

    // Try to restore from sessionStorage first (only if user had selected something before)
    const savedPromptId = sessionStorage.getItem("selectedPromptId");

    if (savedPromptId) {
      // Restore the previously selected prompt
      const savedPrompt = controller.savedPromptTemplates.find((p) => p.id === savedPromptId);
      if (savedPrompt) {
        console.log("🔄 Restoring from sessionStorage:", savedPrompt.shortName);
        controller.setEditPromptValue(savedPrompt.prompt);
        controller.setSavedPromptName(savedPrompt.shortName);
        setIsInitialized(true);
        return;
      }
    }

    // Don't auto-load any prompt initially - let user choose from dropdown or type new one
    // Only set default if there's no existing value
    if (!controller.editPromptValue) {
      console.log("🟢 Initializing with empty prompt - user can select or type new");
      controller.setEditPromptValue("");
      controller.setSavedPromptName("");
    } else {
      console.log("🔄 Using existing controller values:", controller.savedPromptName);
    }

    setIsInitialized(true);
  }, [controller.savedPromptTemplates, isInitialized]);

  const handleCancel = () => {
    controller.setEditPromptValue("");
    controller.setSavedPromptName("");
    sessionStorage.removeItem("selectedPromptId");
    history.push("/");
  };

  const handleSavePrompt = async (prompt: string, savedPromptName: string) => {
    if (prompt.length < 10) {
      throw new Error("Please enter at least 10 characters for your prompt");
    }

    if (!savedPromptName || !savedPromptName.trim()) {
      throw new Error("Please provide a name for your prompt");
    }

    const request = {
      prompt,
      default_prompt: DEFAULT_PROMPT,
      shortName: savedPromptName.trim(),
    };

    try {
      await savePromptToHistory(request);
      console.log("Saved prompt template to /prompt_history:", request);
      controller.setSavedPromptsRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Error saving prompt template to /prompt_history:", error);
      throw error;
    }
  };

  const handleOpenInsights = async (prompt: string, shortName: string) => {
    try {
      if (prompt.length < 10) {
        controller.setDashboardStatus("Please enter at least 10 characters for your prompt");
        setTimeout(() => controller.setDashboardStatus(""), 3000);
        return;
      }

      // First, try to extract data from Pivot Table (if selection is in one)
      let tableData = await extractPivotTableDataIfPresent();

      // If no Pivot Table data, fall back to regular table/selection extraction
      if (!tableData) {
        console.log("📊 No Pivot Table found, using standard data extraction");
        tableData = await extractSelectedData();
      } else {
        console.log("📊 Successfully extracted data from Pivot Table");
      }

      let dataKey: string | null = null;

      // Store table data in sessionStorage if available
      if (
        tableData &&
        tableData.headers &&
        tableData.headers.length > 0 &&
        tableData.rows &&
        tableData.rows.length > 0
      ) {
        dataKey = `ai-insights-data-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        try {
          // Validate data structure before storing
          const dataToStore = {
            headers: tableData.headers,
            rows: tableData.rows,
          };

          // Verify the structure is correct
          if (!Array.isArray(dataToStore.headers) || !Array.isArray(dataToStore.rows)) {
            console.error("❌ Invalid data structure - headers or rows is not an array:", {
              headersIsArray: Array.isArray(dataToStore.headers),
              rowsIsArray: Array.isArray(dataToStore.rows),
              tableData,
            });
            dataKey = null;
          } else {
            sessionStorage.setItem(dataKey, JSON.stringify(dataToStore));
            console.log("✅ Stored table data in sessionStorage:", {
              key: dataKey,
              headers: dataToStore.headers.length,
              rows: dataToStore.rows.length,
              sampleHeaders: dataToStore.headers.slice(0, 3),
              firstRowSample: dataToStore.rows[0]?.slice(0, 3),
            });
          }
        } catch (error) {
          console.error("❌ Failed to store table data in sessionStorage:", error);
          dataKey = null;
        }
      } else {
        console.warn("⚠️ No table data extracted from selection:", {
          hasTableData: !!tableData,
          hasHeaders: !!(tableData && tableData.headers),
          headersLength: tableData?.headers?.length || 0,
          hasRows: !!(tableData && tableData.rows),
          rowsLength: tableData?.rows?.length || 0,
        });
      }

      // Navigate to insights with both prompt and dataKey, and pass through shortName (if any)
      await controller.openInsightsDialogWithPrompt(prompt, dataKey, shortName);
      controller.setDashboardStatus("Opening insights with your custom prompt...");
      // Do NOT redirect back to home here; let the user stay on the AI Insights page
    } catch (error) {
      console.error("Error opening insights dialog:", error);
      controller.setDashboardStatus("Error opening insights dialog. Please try again.");
      setTimeout(() => controller.setDashboardStatus(""), 3000);
    }
  };

  return (
    <>
      <EditPromptUI
        editPromptValue={controller.editPromptValue}
        setEditPromptValue={controller.setEditPromptValue}
        savedPromptName={controller.savedPromptName}
        setSavedPromptName={controller.setSavedPromptName}
        savedPromptsRefresh={controller.savedPromptsRefresh}
        savedPromptTemplates={controller.savedPromptTemplates}
        promptHistory={controller.promptHistory}
        onCancel={handleCancel}
        onSavePrompt={handleSavePrompt}
        onOpenInsights={handleOpenInsights}
        loadPromptDataIfNeeded={controller.loadPromptDataIfNeeded} // ✅ ADDED THIS LINE
      />
    </>
  );
};

export default EditPromptRoute;